"use client";
import { useState, useEffect, useMemo } from "react";
import { MousePointerClick } from "lucide-react";
import { trackEvent } from "@/lib/trackEvent";

function getMinPrice(forms) {
  let min = Infinity;
  for (const form of forms) {
    for (const strength of form.strengths || []) {
      const groups = strength.ingredients?.length > 0 ? strength.ingredients : [strength];
      for (const group of groups) {
        for (const c of group.counts || []) {
          if (c.price < min) min = c.price;
        }
      }
    }
  }
  return min === Infinity ? null : min;
}

function StepPills({ items, getKey, getLabel, activeKey, onSelect, locked }) {
  if (items.length === 0) {
    return (
      <div className="pf-pill-group">
        {[0, 1, 2].map((i) => (
          <span key={i} className="pf-pill pf-pill-skeleton" aria-hidden="true" />
        ))}
      </div>
    );
  }

  return (
    <div className="pf-pill-group">
      {items.map((item) => {
        const key = getKey(item);
        return (
          <button
            key={key}
            type="button"
            disabled={locked}
            onClick={() => onSelect(item)}
            className={`pf-pill ${activeKey === key ? "active" : ""}`}
          >
            {getLabel(item)}
          </button>
        );
      })}
    </div>
  );
}

export default function ProductForm({ product, onFormChange }) {
  const [form, setForm] = useState(null);
  const [strength, setStrength] = useState(null);
  const [ingredient, setIngredient] = useState(null);
  const [count, setCount] = useState(null);
  const [filler, setFiller] = useState("");
  const [flavor, setFlavor] = useState("");

  const forms = product.options.forms || [];
  const minPrice = useMemo(() => getMinPrice(forms), [forms]);
  const ingredients = strength?.ingredients || [];
  const counts = ingredients.length > 0 ? (ingredient?.counts || []) : (strength?.counts || []);

  // Preview chain: falls back to the only possible value when a step is
  // unambiguous (e.g. a product with a single form), so real options can be
  // shown disabled instead of a generic placeholder.
  const previewForm = form || (forms.length === 1 ? forms[0] : null);
  const previewStrengths = previewForm?.strengths || [];
  const previewStrength = strength || (previewStrengths.length === 1 ? previewStrengths[0] : null);
  const previewIngredients = previewStrength?.ingredients || [];
  const previewIngredient = ingredient || (previewIngredients.length === 1 ? previewIngredients[0] : null);
  const previewCounts = previewIngredients.length > 0
    ? (previewIngredient?.counts || [])
    : (previewStrength?.counts || []);
  const previewFillers = previewForm?.fillers || [];
  const previewFlavors = previewForm?.flavors || [];

  const productHasIngredients = forms.some((f) => f.strengths?.some((s) => s.ingredients?.length > 0));
  const productHasFillers = forms.some((f) => f.fillers?.length > 0);
  const productHasFlavors = forms.some((f) => f.flavors?.length > 0);

  const strengthUnlocked = !!form;
  const ingredientUnlocked = !!strength && previewIngredients.length > 0;
  const countUnlocked = ingredients.length > 0 ? !!ingredient : !!strength;
  const fillerUnlocked = !!form && previewFillers.length > 0;
  const flavorUnlocked = !!form && previewFlavors.length > 0;

  useEffect(() => {
    setStrength(null);
    setIngredient(null);
    setCount(null);
    setFiller("");
    setFlavor("");
  }, [form]);

  useEffect(() => {
    setIngredient(null);
    setCount(null);
  }, [strength]);

  useEffect(() => setCount(null), [ingredient]);

  return (
    <div id="product-form" className="pf-card">

      {/* FORM SELECTOR */}
      <div className="pf-step">
        <div className="pf-step-label highlight">
          <MousePointerClick className="pf-step-icon" aria-hidden="true" />
          <span>Start by selecting a form for your price</span>
        </div>

        <StepPills
          items={forms}
          getKey={(f) => f.name}
          getLabel={(f) => f.name}
          activeKey={form?.name}
          locked={false}
          onSelect={(f) => {
            setForm(f);
            if (typeof onFormChange === "function") {
              onFormChange(f);
            }
          }}
        />
      </div>

      {/* STRENGTH SELECTOR */}
      <div className="pf-step" data-locked={!strengthUnlocked}>
        <div className="pf-step-label">
          <span>{previewForm?.strengthLabel || "Strength"}</span>
          {!strengthUnlocked && <span className="pf-step-hint">Select a form first</span>}
        </div>

        <StepPills
          items={previewStrengths}
          getKey={(s) => s.strength}
          getLabel={(s) => s.strength}
          activeKey={strength?.strength}
          locked={!strengthUnlocked}
          onSelect={setStrength}
        />
      </div>

      {/* INGREDIENTS SELECTOR */}
      {productHasIngredients && (
        <div className="pf-step" data-locked={!ingredientUnlocked}>
          <div className="pf-step-label">
            <span># of Ingredients</span>
            {!ingredientUnlocked && (
              <span className="pf-step-hint">
                {!strength ? "Select a strength first" : "Not available for this strength"}
              </span>
            )}
          </div>

          {ingredientUnlocked && (
            <div className="ingredients-info-box">
              <p className="ingredients-info-intro">
                Choose how many active ingredients you'd like compounded into your prescription.
                Your prescriber will select from the following available ingredients:
              </p>
              <div className="ingredients-tag-list">
                {[
                  "Estriol (E3)",
                  "Estradiol (E2)",
                  "Dehydroepiandrosterone (DHEA)",
                  "Pregnenolone",
                  "Progesterone",
                  "Testosterone",
                ].map((name) => (
                  <span key={name} className="ingredient-tag">{name}</span>
                ))}
              </div>
            </div>
          )}

          <StepPills
            items={previewIngredients}
            getKey={(i) => i.count_ingredients}
            getLabel={(i) => `${i.count_ingredients} Ingredient${i.count_ingredients !== 1 ? "s" : ""}`}
            activeKey={ingredient?.count_ingredients}
            locked={!ingredientUnlocked}
            onSelect={setIngredient}
          />
        </div>
      )}

      {/* COUNT SELECTOR */}
      <div className="pf-step" data-locked={!countUnlocked}>
        <div className="pf-step-label">
          <span>Quantity</span>
          {!countUnlocked && (
            <span className="pf-step-hint">
              Select {ingredients.length > 0 ? "an ingredient count" : "a strength"} first
            </span>
          )}
        </div>

        <StepPills
          items={previewCounts}
          getKey={(c) => c.count}
          getLabel={(c) => c.count}
          activeKey={count?.count}
          locked={!countUnlocked}
          onSelect={(c) => setCount(c)}
        />
      </div>

      {/* FILLER SELECTOR */}
      {productHasFillers && (
        <div className="pf-step" data-locked={!fillerUnlocked}>
          <div className="pf-step-label">
            <span>Filler</span>
            {!fillerUnlocked && (
              <span className="pf-step-hint">
                {!form ? "Select a form first" : "Not available for this form"}
              </span>
            )}
          </div>

          <select
            value={filler}
            disabled={!fillerUnlocked}
            onChange={(e) => setFiller(e.target.value)}
            className="filler-select"
            aria-label="Filler"
          >
            <option value="">Select Filler</option>
            {previewFillers.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
      )}

      {/* FLAVOR SELECTOR */}
      {productHasFlavors && (
        <div className="pf-step" data-locked={!flavorUnlocked}>
          <div className="pf-step-label">
            <span>Flavor</span>
            {!flavorUnlocked && (
              <span className="pf-step-hint">
                {!form ? "Select a form first" : "Not available for this form"}
              </span>
            )}
          </div>

          <select
            value={flavor}
            disabled={!flavorUnlocked}
            onChange={(e) => setFlavor(e.target.value)}
            className="filler-select"
            aria-label="Flavor"
          >
            <option value="">Select Flavor</option>
            {previewFlavors.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
      )}

      {/* PRICE BOX */}
      <div id="price-box">
        <div className="price-row">
          {count
            ? `Price: $${count.price.toFixed(2)}`
            : minPrice != null
              ? `Price: as low as $${minPrice.toFixed(2)}`
              : "Select options above for your quote"}
        </div>
        <p className="price-disclaimer">
          <strong>Please Note:</strong> Due to market volatility, prices are subject to change. Please <a href="/contact-us/">contact us</a> for the most current pricing.
        </p>
      </div>

      {/* CTA PANEL */}
      <div className="cta-panel">
        <h2>Ready to Move Forward?</h2>
        <p>Select the action that best fits how you'd like us to handle the prescription.</p>

        <div className="cta-actions">
          <a
            href="/patient/"
            className="cta-card primary"
            onClick={() => trackEvent("generate_lead", count?.price, product.slug)}
          >
            <div className="cta-icon">📄</div>
            <div>
              <span>Transfer the Prescription</span>
              <p>Send us your current Rx and we'll handle the rest.</p>
            </div>
          </a>

          <a
            href="tel:18448227379"
            className="cta-card secondary"
            onClick={() => {
              trackEvent("generate_lead", count?.price, product.slug);
              trackEvent("phone_number", 50, product.slug);
            }}
          >
            <div className="cta-icon">📞</div>
            <div>
              <span>Schedule a Call</span>
              <p>Talk with our pharmacy team to get started.</p>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}
